import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useRef, useEffect } from 'react';
import DataTable from 'datatables.net-dt';
import 'datatables.net-dt/css/dataTables.dataTables.css';
import $ from 'jquery';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

// Custom CSS with Tailwind CSS classes
const customStyles = `
  .date-picker-container {
    @apply relative w-32;
  }
  .react-datepicker__input-container input {
    @apply h-8 w-full rounded-md border border-gray-300 px-2 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all duration-150;
  }
  .date-picker-icon {
    @apply absolute right-2 top-1/2 -translate-y-1/2 text-gray-400;
  }

  /* 📉 Fix large month/year text */
  .react-datepicker__current-month {
    font-size: 0.875rem !important; /* text-sm */
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.25rem;
  }
  .react-datepicker__header {
    @apply bg-white p-1 border-b border-gray-300;
  }
  .react-datepicker__day-name {
    @apply text-gray-500 text-xs w-6 h-6 flex items-center justify-center;
  }
  .react-datepicker__day {
    @apply text-gray-800 text-xs w-6 h-6 flex items-center justify-center rounded-full hover:bg-orange-100;
  }
  .react-datepicker__day--selected {
    @apply bg-orange-500 text-white;
  }
  .react-datepicker__navigation {
    @apply top-2;
  }
  .react-datepicker__navigation--previous {
    @apply left-2;
  }
  .react-datepicker__navigation--next {
    @apply right-2;
  }

  /* 🛠️ Year dropdown fix */
  .react-datepicker__year-read-view,
  .react-datepicker__month-read-view {
    font-size: 0.75rem !important;
  }
  .react-datepicker__year-dropdown,
  .react-datepicker__month-dropdown {
    font-size: 0.75rem !important;
  }
  .react-datepicker__year-option,
  .react-datepicker__month-option {
    font-size: 0.75rem !important;
  }

  /* ✅ View Button Styling */
  .btn-view {
    @apply text-white focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 px-5 py-2 rounded-md text-sm transition-all duration-150;
    background-color: #5C9347;
  }

  /* ✅ Download as Dropdown styled like a button */
  .form-select-download {
    @apply text-white text-sm px-4 py-2 rounded-md transition-all duration-150 border-0;
    background-color: #5C9347;
    appearance: none;
  }
  .form-select-download:disabled {
    @apply bg-gray-200 cursor-not-allowed text-gray-400;
  }
`;



const UserListReport = () => {
    const [users, setUsers] = useState([]);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [showEntries, setShowEntries] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedFormat, setSelectedFormat] = useState('');
    const [filters, setFilters] = useState({
        name: { operator: 'contains', value: '' },
        surname: { operator: 'contains', value: '' },
        email: { operator: 'contains', value: '' },
        phone: { operator: 'contains', value: '' },
        address: { operator: 'contains', value: '' },
        status: { operator: '=', value: '' },
    });
    const tableRef = useRef(null);
    const dataTableRef = useRef(null);
    const searchInputRef = useRef(null);
    const [dataFetched, setDataFetched] = useState(false);

    // Placeholder API call simulation
    const fetchUserData = async () => {
        // Replace with actual API call
        const response = await new Promise((resolve) => {
            setTimeout(() => {
                resolve([
                    {
                        id: 1,
                        name: 'Kathryn',
                        surname: 'Murphy',
                        email: 'osgoodwy@gmail.com',
                        phone: '+1234567890',
                        address: '123 Main St, City',
                        status: 'Active',
                        password: 'AutoGen123!',
                    },
                    {
                        id: 2,
                        name: 'Annette',
                        surname: 'Black',
                        email: 'redaniel@gmail.com',
                        phone: '+1987654321',
                        address: '456 Elm St, Town',
                        status: 'Inactive',
                        password: 'AutoGen456!',
                    },
                ]);
            }, 1000);
        });
        setUsers(response);
        setDataFetched(true);
    };

    const handleViewClick = () => {
        if (startDate && endDate) {
            fetchUserData();
        } else {
            alert('Please select both start and end dates.');
        }
    };

    const handleFilterChange = (field, key, value) => {
        setFilters((prev) => ({
            ...prev,
            [field]: { ...prev[field], [key]: value },
        }));
    };

    const applyFilters = (data) => {
        return data.filter((user) => {
            return Object.keys(filters).every((field) => {
                const { operator, value } = filters[field];
                if (!value) return true;
                const userValue = user[field]?.toString().toLowerCase() || '';
                const filterValue = value.toLowerCase();
                switch (operator) {
                    case '=':
                        return userValue === filterValue;
                    case '<>':
                        return userValue !== filterValue;
                    case 'like':
                        return userValue.includes(filterValue);
                    case 'contains':
                        return userValue.includes(filterValue);
                    default:
                        return true;
                }
            });
        });
    };

    const filteredUsers = applyFilters(users);

    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.autoTable({
            head: [['Name', 'Surname', 'Email', 'Phone', 'Address', 'Status']],
            body: filteredUsers.map((user) => [
                user.name,
                user.surname,
                user.email,
                user.phone,
                user.address,
                user.status,
            ]),
        });
        doc.save('user_list_report.pdf');
    };

    const exportToExcel = () => {
        const dataForExport = filteredUsers.map((user) => ({
            Name: user.name,
            Surname: user.surname,
            Email: user.email,
            Phone: user.phone,
            Address: user.address,
            Status: user.status,
        }));
        const worksheet = XLSX.utils.json_to_sheet(dataForExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Users');
        XLSX.writeFile(workbook, 'user_list_report.xlsx');
    };

    const handleEmailCredentials = (email, password) => {
        alert(`Emailing credentials to ${email}: Password is ${password}`);
        // Implement actual email logic here
    };

    const handleShowCredentials = (email, password) => {
        alert(`Credentials for ${email}: Password is ${password}`);
    };

    const initializeDataTable = () => {
        if (tableRef.current && !dataTableRef.current) {
            dataTableRef.current = new DataTable(tableRef.current, {
                paging: true,
                pageLength: showEntries,
                lengthChange: false,
                searching: true,
                ordering: true,
                info: false,
                dom: 't',
                columnDefs: [
                    { targets: 2, width: '150px' },
                    { targets: 6, orderable: false, searchable: false, width: '120px' },
                ],
                order: [[0, 'asc']],
                data: filteredUsers,
                drawCallback: function () {
                    if (dataTableRef.current) {
                        attachEventListeners();
                        const pageInfo = this.api().page.info();
                        setCurrentPage(pageInfo.page + 1);
                    }
                },
            });

            if (searchInputRef.current) {
                searchInputRef.current.addEventListener('input', (e) => {
                    if (dataTableRef.current) {
                        dataTableRef.current.search(e.target.value).draw();
                    }
                });
            }
        }
    };

    const attachEventListeners = () => {
        if (!dataTableRef.current) return;

        const menuButtons = tableRef.current?.querySelectorAll('.menu-btn');

        menuButtons?.forEach((btn) => {
            btn.onclick = null;
            const userId = parseInt(btn.getAttribute('data-user-id'));
            const user = filteredUsers.find((u) => u.id === userId);
            if (user) {
                btn.onclick = (e) => {
                    const tooltip = btn.nextElementSibling;
                    tooltip.classList.toggle('d-block');
                    document.addEventListener(
                        'click',
                        (event) => {
                            if (!btn.contains(event.target) && !tooltip.contains(event.target)) {
                                tooltip.classList.remove('d-block');
                            }
                        },
                        { once: true }
                    );
                };
            }
        });
    };

    const handlePreviousPage = () => {
        if (dataTableRef.current && currentPage > 1) {
            dataTableRef.current.page('previous').draw('page');
        }
    };

    const handleNextPage = () => {
        if (dataTableRef.current && currentPage * showEntries < filteredUsers.length) {
            dataTableRef.current.page('next').draw('page');
        }
    };

    const handlePageChange = (page) => {
        if (dataTableRef.current) {
            dataTableRef.current.page(page - 1).draw('page');
        }
    };

    useEffect(() => {
        window.jQuery = window.$ = $;
        if (dataFetched) {
            initializeDataTable();
        }
        return () => {
            if (dataTableRef.current) {
                dataTableRef.current.destroy();
                dataTableRef.current = null;
            }
        };
    }, [dataFetched]);

    useEffect(() => {
        if (dataTableRef.current) {
            dataTableRef.current.page.len(showEntries).draw();
        }
    }, [showEntries]);

    useEffect(() => {
        if (dataTableRef.current && dataFetched) {
            dataTableRef.current.clear().rows.add(filteredUsers).draw();
        }
    }, [users, JSON.stringify(filters)]);

    return (
        <>
            <style>{customStyles}</style>
            <div className="card h-100 p-0 radius-12">
                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                    <div className="d-flex align-items-center gap-3">
                        <span>Select Dates</span>
                        <div className="date-picker-container">
                            <DatePicker
                                selected={startDate}
                                onChange={(date) => setStartDate(date)}
                                placeholderText="Start Date"
                                dateFormat="MM/dd/yyyy"
                                popperPlacement="bottom-start"
                            />
                            <Icon icon="bi:calendar" className="date-picker-icon" />
                        </div>
                        <div className="date-picker-container">
                            <DatePicker
                                selected={endDate}
                                onChange={(date) => setEndDate(date)}
                                placeholderText="End Date"
                                dateFormat="MM/dd/yyyy"
                                popperPlacement="bottom-end"
                            />
                            <Icon icon="bi:calendar" className="date-picker-icon" />
                        </div>
                        <button
                            onClick={handleViewClick}
                        className="btn btn-yellow text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                        >
                            View
                        </button>
                        {/* <select
                            className="form-select-download"
                            value={selectedFormat}
                            onChange={(e) => {
                                const fmt = e.target.value;
                                if (fmt === 'pdf') {
                                    exportToPDF();
                                } else if (fmt === 'excel') {
                                    exportToExcel();
                                }
                                setSelectedFormat('');
                            }}
                            disabled={!startDate || !endDate || !dataFetched}
                        >
                            <option value="">Download as...</option>
                            <option value="pdf">PDF</option>
                            <option value="excel">Excel</option>
                        </select> */}
                    </div>
                </div>
                <div className="card-body p-24">
                    {!dataFetched ? (
                        <p className="text-center text-secondary-light">Please select dates and click View to load data.</p>
                    ) : (
                        <>
                            <div className="filter-row">
                                {['name', 'surname', 'email', 'phone', 'address', 'status'].map((field) => (
                                    <div key={field} className="filter-container">
                                        <select
                                            className="filter-select"
                                            value={filters[field].operator}
                                            onChange={(e) => handleFilterChange(field, 'operator', e.target.value)}
                                        >
                                            <option value="=">=</option>
                                            <option value="<>">≠</option>
                                            <option value="like">Like</option>
                                            <option value="contains">Contains</option>
                                        </select>
                                        <input
                                            type="text"
                                            className="filter-input"
                                            placeholder={`Filter ${field.charAt(0).toUpperCase() + field.slice(1)}`}
                                            value={filters[field].value}
                                            onChange={(e) => handleFilterChange(field, 'value', e.target.value)}
                                        />
                                    </div>
                                ))}
                            </div>
                            <div className="table-responsive scroll-sm">
                                <table ref={tableRef} className="table bordered-table sm-table mb-0" id="userListReportTable">
                                    <thead>
                                        <tr>
                                            <th scope="col">Name</th>
                                            <th scope="col">Surname</th>
                                            <th scope="col">Email</th>
                                            <th scope="col">Phone</th>
                                            <th scope="col">Address</th>
                                            <th scope="col" className="text-center">Status</th>
                                            <th scope="col" className="text-center">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredUsers.map((user) => (
                                            <tr key={user.id}>
                                                <td>
                                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {user.name}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {user.surname}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {user.email}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {user.phone}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {user.address}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <span
                                                        className={`px-24 py-4 radius-4 fw-medium text-sm ${
                                                            user.status === 'Active'
                                                                ? 'bg-success-focus text-success-600 border border-success-main'
                                                                : 'bg-neutral-200 text-neutral-600 border border-neutral-400'
                                                        }`}
                                                    >
                                                        {user.status}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <div className="d-flex align-items-center gap-10 justify-content-center position-relative">
                                                        <button
                                                            type="button"
                                                            className="menu-btn bg-neutral-200 bg-hover-neutral-300 text-secondary-light fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                            data-user-id={user.id}
                                                        >
                                                            <Icon icon="ph:dots-three-vertical-bold" className="menu-icon" />
                                                        </button>
                                                        <div
                                                            className="tooltip-menu position-absolute d-none"
                                                            style={{ top: '100%', right: 0, zIndex: 1000 }}
                                                        >
                                                            <div
                                                                className="tooltip-item"
                                                                onClick={() => handleEmailCredentials(user.email, user.password)}
                                                            >
                                                                Email Credentials
                                                            </div>
                                                            <div
                                                                className="tooltip-item"
                                                                onClick={() => handleShowCredentials(user.email, user.password)}
                                                            >
                                                                Show Credentials
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
                                <span>
                                    Showing {Math.min((currentPage - 1) * showEntries + 1, filteredUsers.length)} to{' '}
                                    {Math.min(currentPage * showEntries, filteredUsers.length)} of {filteredUsers.length} entries
                                </span>
                                <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
                                    <li className="page-item">
                                        <button
                                            className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                                            onClick={handlePreviousPage}
                                            disabled={currentPage === 1}
                                        >
                                            <Icon icon="ep:d-arrow-left" className="" />
                                        </button>
                                    </li>
                                    <li className="page-item">
                                        <button
                                            className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${
                                                currentPage === 1 ? 'bg-primary-600 text-white' : ''
                                            }`}
                                            onClick={() => handlePageChange(1)}
                                        >
                                            1
                                        </button>
                                    </li>
                                    {filteredUsers.length > showEntries && (
                                        <li className="page-item">
                                            <button
                                                className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${
                                                    currentPage === 2 ? 'bg-primary-600 text-white' : ''
                                                }`}
                                                onClick={() => handlePageChange(2)}
                                            >
                                                2
                                            </button>
                                        </li>
                                    )}
                                    <li className="page-item">
                                        <button
                                            className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                                            onClick={handleNextPage}
                                            disabled={currentPage * showEntries >= filteredUsers.length}
                                        >
                                            <Icon icon="ep:d-arrow-right" className="" />
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default UserListReport;